import React, { useState } from 'react';
import axios from 'axios';
import { IoRefreshOutline } from 'react-icons/io5';

const UPLOAD_URL = process.env.REACT_APP_API_VIDEO_UPLOAD_ROUTE;
const CONFIG_UPDATE_URL = process.env.REACT_APP_API_VIDEO_CONFIG_UPDATE_URL;
const AUTH_KEY = process.env.REACT_APP_API_KEY_LOCAL_STORAGE;

const ROTATION_CYCLE = [-90, 0, 90, 180];

function readRotation(key, defaultVal) {
    const stored = localStorage.getItem(key);
    return stored !== null ? parseInt(stored, 10) : defaultVal;
}

function nextRotation(current) {
    const idx = ROTATION_CYCLE.indexOf(current);
    return ROTATION_CYCLE[(idx + 1) % ROTATION_CYCLE.length];
}

function prevRotation(current) {
    const idx = ROTATION_CYCLE.indexOf(current);
    return ROTATION_CYCLE[(idx - 1 + ROTATION_CYCLE.length) % ROTATION_CYCLE.length];
}

export default function GestionVideo() {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [reloadSent, setReloadSent] = useState(false);

    const [rotation1, setRotation1] = useState(() => readRotation('video_rotation', -90));
    const [rotation2, setRotation2] = useState(() => readRotation('video_rotation_v2', 90));
    const [rotationSaving, setRotationSaving] = useState(false);
    const [rotationError, setRotationError] = useState(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setMessage(null);
        setError(null);
        setProgress(0);
        setReloadSent(false);
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('video', file);
        const token = localStorage.getItem(AUTH_KEY);

        setUploading(true);
        setMessage(null);
        setError(null);

        try {
            await axios.post(UPLOAD_URL, formData, {
                withCredentials: true,
                headers: token ? { Authorization: token } : undefined,
                onUploadProgress: (e) => {
                    const pct = Math.round((e.loaded * 100) / e.total);
                    setProgress(pct);
                },
            });
            setMessage('Video procesado exitosamente. Ya está disponible para reproducción.');
            setFile(null);
            setProgress(0);
        } catch (err) {
            setError(err.response?.data || 'Error al subir el video. Intentá nuevamente.');
        } finally {
            setUploading(false);
        }
    };

    const handleReloadScreens = () => {
        localStorage.setItem('video_reload', Date.now().toString());
        setReloadSent(true);
    };

    const saveRotation = async (newRotation1, newRotation2) => {
        setRotationSaving(true);
        setRotationError(null);
        try {
            const token = localStorage.getItem(AUTH_KEY);
            await axios.post(CONFIG_UPDATE_URL,
                { rotation: newRotation1, rotationV2: newRotation2 },
                {
                    withCredentials: true,
                    headers: token ? { Authorization: token } : undefined,
                }
            );
            // También actualiza localStorage para feedback inmediato en mismo navegador
            localStorage.setItem('video_rotation', newRotation1.toString());
            localStorage.setItem('video_rotation_v2', newRotation2.toString());
        } catch (err) {
            setRotationError('No se pudo guardar la rotación. Intentá de nuevo.');
        } finally {
            setRotationSaving(false);
        }
    };

    const changeRotation1 = (direction) => {
        const next = direction === 'next' ? nextRotation(rotation1) : prevRotation(rotation1);
        setRotation1(next);
        saveRotation(next, rotation2);
    };

    const changeRotation2 = (direction) => {
        const next = direction === 'next' ? nextRotation(rotation2) : prevRotation(rotation2);
        setRotation2(next);
        saveRotation(rotation1, next);
    };

    return (
        <div className="container mt-4">
            <h4>Gestión de Video</h4>
            <hr />

            {/* Upload */}
            <div className="card p-4 mb-4" style={{ maxWidth: '500px' }}>
                <h6 className="fw-semibold mb-3">Subir video</h6>
                <div className="mb-3">
                    <label className="form-label fw-semibold">Seleccionar video (.mp4)</label>
                    <input
                        type="file"
                        className="form-control"
                        accept="video/mp4"
                        onChange={handleFileChange}
                        disabled={uploading}
                    />
                    {file && (
                        <small className="text-muted mt-1 d-block">
                            {file.name} — {(file.size / 1024 / 1024).toFixed(1)} MB
                        </small>
                    )}
                </div>

                {uploading && (
                    <div className="mb-3">
                        <div className="progress">
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                style={{ width: `${progress}%` }}
                            >
                                {progress < 100 ? `${progress}%` : 'Procesando...'}
                            </div>
                        </div>
                    </div>
                )}

                {message && (
                    <div className="mb-3">
                        <div className="alert alert-success py-2 mb-2">{message}</div>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleReloadScreens}
                            disabled={reloadSent}
                        >
                            <IoRefreshOutline className="me-1" />
                            {reloadSent ? 'Señal enviada ✓' : 'Recargar pantallas de video'}
                        </button>
                        {reloadSent && (
                            <small className="text-muted d-block mt-1">
                                Las pantallas actualizarán el video en los próximos 10 segundos.
                            </small>
                        )}
                    </div>
                )}

                {error && <div className="alert alert-danger py-2">{error}</div>}

                <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={!file || uploading}
                >
                    {uploading ? 'Procesando...' : 'Subir y convertir'}
                </button>
            </div>

            {/* Rotación */}
            <div className="card p-4" style={{ maxWidth: '500px' }}>
                <h6 className="fw-semibold mb-1">Rotación de pantallas</h6>
                <small className="text-muted d-block mb-3">
                    Los cambios se aplican en todos los dispositivos en menos de 10 segundos.
                </small>

                {rotationError && <div className="alert alert-danger py-2 mb-3">{rotationError}</div>}

                <div className="d-flex align-items-center justify-content-between mb-3 p-2 border rounded">
                    <span className="fw-semibold" style={{ minWidth: '110px' }}>Pantalla /video</span>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => changeRotation1('prev')}
                            disabled={rotationSaving}
                        >
                            ◄
                        </button>
                        <span className="text-center fw-bold" style={{ minWidth: '50px' }}>{rotation1}°</span>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => changeRotation1('next')}
                            disabled={rotationSaving}
                        >
                            ►
                        </button>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                    <span className="fw-semibold" style={{ minWidth: '110px' }}>Pantalla /video-v2</span>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => changeRotation2('prev')}
                            disabled={rotationSaving}
                        >
                            ◄
                        </button>
                        <span className="text-center fw-bold" style={{ minWidth: '50px' }}>{rotation2}°</span>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => changeRotation2('next')}
                            disabled={rotationSaving}
                        >
                            ►
                        </button>
                    </div>
                </div>

                {rotationSaving && (
                    <small className="text-muted d-block mt-2">Guardando...</small>
                )}
            </div>
        </div>
    );
}
